<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { ChatAgent, ChatMessage, ChatPart, ChatSurface } from "../types/chat";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Conversation, ConversationContent, ConversationScrollButton } from "./ai-elements/conversation";
import { Message, MessageContent, MessageResponse } from "./ai-elements/message";
import { Reasoning, ReasoningContent, ReasoningTrigger } from "./ai-elements/reasoning";
import { Loader } from "./ai-elements/loader";
import BlockPart from "./parts/block-part.vue";
import ErrorPart from "./parts/error-part.vue";

/**
 * Transcript renderer for the takeover, composed entirely from the AI Elements Vue family. The ONLY
 * custom piece is `BlockPart`, which renders our building blocks for `render_*` tool calls.
 *
 *  - scroll      → `Conversation`/`ConversationContent`/`ConversationScrollButton`
 *                 (`vue-stick-to-bottom`: auto-sticks to the bottom, offers a jump-to-bottom button).
 *  - `message`   → `Message`/`MessageContent`/`MessageResponse` (markdown via `vue-stream-markdown`).
 *  - `reasoning` → `Reasoning`/`ReasoningTrigger`/`ReasoningContent` (collapsible "thinking" panel).
 *  - busy        → `Loader` (streaming indicator).
 *  - `tool`      → `BlockPart` (our building block while pending; an AI Elements `Tool` card once answered).
 *  - `error`     → `ErrorPart` (failed / cancelled turn).
 */
const props = defineProps<{
  messages: ChatMessage[];
  agent?: ChatAgent | null;
  busy?: boolean;
  surface?: ChatSurface;
  showToolDetails?: boolean;
}>();

const emit = defineEmits<{
  (
    e: "submit-tool",
    payload: { invocationId: string; toolName: string; result: Record<string, unknown> },
  ): void;
  (
    e: "cancel-tool",
    payload: { invocationId: string; toolName: string },
  ): void;
  (
    e: "confirm-tool",
    payload: { invocationId: string; toolName: string; approve: boolean; summary?: string },
  ): void;
}>();

const { t } = useI18n({ useScope: "local" });

/** Parts sorted by first-seen sequence for stable ordering. */
function orderedParts(message: ChatMessage): ChatPart[] {
  return [...message.parts].sort((a, b) => a.sequence - b.sequence);
}

/**
 * Whether to show the bottom "thinking" indicator: the assistant is busy and isn't currently
 * streaming visible prose (the streaming markdown is its own indicator). Covers the initial latency,
 * tool preparation, and reasoning-without-a-panel gaps so the surface never looks frozen.
 */
const showThinking = computed(() => {
  if (!props.busy) {
    return false;
  }
  const last = props.messages[props.messages.length - 1];
  if (!last || last.role !== "assistant") {
    return true;
  }
  const streamingText = last.parts.some(
    (p) => p.type === "message" && p.isStreaming && p.text.length > 0,
  );
  return !streamingText;
});

const agentIcon = computed(() => props.agent?.icon);

function avatarGlyph(): string {
  const agent = props.agent;
  if (!agent) {
    return "✨";
  }
  return agent.avatar ?? agent.name.charAt(0).toUpperCase();
}
</script>

<template>
  <Conversation
    class="min-h-0 flex-1"
    :aria-label="t('conversation')"
    :aria-busy="busy ? 'true' : 'false'"
  >
    <ConversationContent class="mx-auto w-full max-w-3xl gap-6">
      <p class="sr-only" role="status">{{ busy ? t("responding") : "" }}</p>
      <div
        v-for="message in messages"
        :key="message.id"
        :class="['flex gap-3', message.role === 'user' ? 'justify-end' : 'justify-start']"
        :data-role="message.role"
      >
        <Avatar v-if="message.role !== 'user'" class="size-7 shrink-0 rounded-lg">
          <AvatarFallback class="rounded-lg bg-primary/10 text-xs text-primary">
            <Icon v-if="agentIcon" :name="agentIcon" mode="svg" class="size-4" />
            <template v-else>{{ avatarGlyph() }}</template>
          </AvatarFallback>
        </Avatar>

        <Message
          :from="message.role"
          :class="message.role === 'user' ? 'max-w-[80%]' : 'min-w-0 max-w-none flex-1'"
        >
          <MessageContent :class="message.role === 'user' ? 'min-w-0' : 'min-w-0 w-full'">
            <template v-for="part in orderedParts(message)" :key="part.key">
              <!-- User turns render verbatim; assistant prose streams as markdown. Empty assistant
                   placeholder parts (e.g. the opening frame before any text) are skipped. -->
              <p
                v-if="part.type === 'message' && message.role === 'user'"
                class="whitespace-pre-wrap break-words"
              >{{ part.text }}</p>

              <MessageResponse
                v-else-if="part.type === 'message' && (part.text.length > 0 || part.isStreaming)"
                :content="part.text"
              />

              <Reasoning
                v-else-if="part.type === 'reasoning'"
                :is-streaming="part.isStreaming"
                class="w-full"
              >
                <ReasoningTrigger />
                <ReasoningContent :content="part.text" />
              </Reasoning>

              <BlockPart
                v-else-if="part.type === 'tool' && part.tool"
                :tool="part.tool"
                :text="part.text"
                :disabled="!!busy"
                :surface="surface"
                :show-tool-details="showToolDetails"
                @submit="(payload) => emit('submit-tool', payload)"
                @cancel="(payload) => emit('cancel-tool', payload)"
                @confirm="(payload) => emit('confirm-tool', payload)"
              />

              <ErrorPart
                v-else-if="part.type === 'error'"
                :text="part.text"
                :error="part.error"
              />
            </template>
          </MessageContent>
        </Message>
      </div>

      <!-- Bottom "thinking" indicator: a visible streaming state while the assistant works and no
           prose is actively streaming (initial latency, tool prep, reasoning without a panel). -->
      <div
        v-if="showThinking"
        class="flex items-center gap-2 text-sm text-muted-foreground"
        data-slot="chat-thinking"
        aria-live="polite"
      >
        <Loader :size="16" />
        <span>{{ t("thinking") }}</span>
      </div>
    </ConversationContent>

    <ConversationScrollButton />
  </Conversation>
</template>

<i18n lang="yaml">
en:
  responding: The assistant is responding…
  thinking: Thinking…
  conversation: Conversation
</i18n>
